<template>
	<div class="overlay-dialog">
		<h2
			class="overlay-dialog__title"
			v-html="$t('account.userDetailEdit.title')"
		/>
		<GeneralMessage
			ref="message"
			v-if="response.message"
			:message="response.message"
			:type="response.type"
			@close="response.message = ''"
		/>
		<ValidationObserver
			ref="form"
			v-slot="{ invalid, errors }"
			tag="form"
			@submit.prevent="submit"
			novalidate
		>

			<ValidationProvider
				class="input-wrapper"
				name="firstname"
				rules="required"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
			>
				<FormInput
					type="text"
					name="firstname"
					:placeholder="$t('forms.labels.firstname')"
					:label="$t('forms.labels.firstname')"
					v-model="form.userDetail.firstName"
					:error="invalid && validated"
					:disabled="disabled"
					@input="onInput"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

			<ValidationProvider
				class="input-wrapper"
				name="lastname"
				rules="required"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
			>
				<FormInput
					type="text"
					name="lastname"
					:placeholder="$t('forms.labels.lastname')"
					:label="$t('forms.labels.lastname')"
					v-model="form.userDetail.lastName"
					:error="invalid && validated"
					:disabled="disabled"
					@input="onInput"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

			<!-- EMAIL -->
			<ValidationProvider
				class="input-wrapper"
				name="email"
				rules="required|email"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				ref="registerEmail"
				:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
			>
				<FormInput
					type="email"
					name="email"
					autocomplete="email"
					:placeholder="$t('forms.labels.email')"
					:label="$t('forms.labels.email')"
					v-model="form.userDetail.email"
					:error="invalid && validated"
					:disabled="disabled"
					@input="onInput"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

			<ValidationProvider
				class="input-wrapper"
				name="phone"
				rules="required|phone"
				v-slot="{ errors, invalid, validated }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty'), phone: $t('forms.errors.phone') }"
			>
				<FormInput
					type="tel"
					name="phone"
					:placeholder="$t('forms.labels.phone')"
					:label="$t('forms.labels.phone')"
					v-model="form.userDetail.phoneNumber"
					:error="errors.length > 0"
					:disabled="disabled"
					@input="onInput"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
				<span class="additional-info" v-html="$t('login.phoneHint')"/>
			</ValidationProvider>

			<Button
				type="submit"
				level="primary"
				:text="$t('account.userDetailEdit.save')"
				:disabled="loading"
			/>
		</ValidationObserver>
	</div>
</template>

<script>
import Vue from "vue"
import { mapGetters } from "vuex"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputTextarea from "@/components/FormInputTextarea"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputSelect from "@/components/FormInputSelectCustom"
import GeneralMessage from '@/components/GeneralMessage'
import Button from '@/components/Button'
import GenericMixin from '@/mixins/GenericVueMixin'

export default {
  mixins: [
    GenericMixin,
  ],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputTextarea,
		FormInputCheckbox,
		FormInputSelect,
		GeneralMessage,
		Button,
	},
	data() {
		return {
			form: {
				userDetail: {
					firstName: "",
					lastName: "",
					phoneNumber: "",
					email: '',
				},
			},
			response: {
				message: '',
				type: '',
			},
			edited: false,
		};
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn",
			"accountUserDetail",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		disabled() {
			return this.loading;
		},
	},
	methods: {
		onInput() {
			this.edited = true;
		},
		async submit() {
			this.loading = true
			const isValid = await this.$refs.form.validate()
			if (isValid) {
				this.response.message = '';
				try {
					await this.$store.dispatch('account/updateUser', {
						...this.accountUserDetail,
						...this.form.userDetail,
					})
					this.edited = false;
					this.response.message = this.$t('account.userDetailEdit.success');
					this.response.type = 'success';
				}
				catch(err) {
          if (err.isMaintenanceError) {
            this.onMaintenanceMode({ callback: () => this.submit(), retry: true })
          } else {
            let notificationMessage = err?.response?.data?.message || this.$t('forms.errors.generic')
            this.response.message = notificationMessage;
            this.response.type = 'error';
          }

				}
				this.$parent.scrollTop();
			}
			this.loading = false
		},
		async onClose() {
			if (this.edited) {
				const confirmed = await this.$store.dispatch('dialog/showDialog', {
					title: this.$t('account.userDetailEdit.dialogTitle'),
					message: this.$t('account.userDetailEdit.dialogMessage'),
					confirm: this.$t('account.userDetailEdit.dialogConfirm'),
					cancel: this.$t('account.userDetailEdit.dialogCancel'),
				});
				if (confirmed) {
					return;
				}
			}
			this.$emit('close');
		}
	},
	created() {
		if (this.accountUserDetail) {
			Object.keys(this.form.userDetail).forEach(key => {
				if (this.accountUserDetail[key] !== undefined) {
					Vue.set(this.form.userDetail, key, ''+this.accountUserDetail[key]);
				}
			});
		}
	}
}
</script>

<style>

</style>
